import { gql } from '@apollo/client';
import {
  Container,
  ContentWrapper,
  EntryHeader,
  Footer,
  Header,
  Main,
  NavigationMenu,
  SEO,
} from '../../components';
import * as MENUS from '../../constants/menus';
import { BlogInfoFragment } from '../../fragments/GeneralSettings';
import { ArtikkelSEOFragment, OptimizedImageFragment } from '../../fragments/artikler/ArtikkelSEOFragment';
import { isBlogPost } from '../../constants/categories';
import React from 'react';
import Image from 'next/image';
import Head from 'next/head';
import styles from '../../styles/Single.module.scss';
import Link from 'next/link';
import { MediaBoosterSettingsFragment } from '../../fragments/MediaBoosterSettings';
import ContactBoxes from '../../components/ContactPage/ContactBoxes/ContactBoxes';
// Dynamic import for RelatedArticles to reduce initial bundle size
import dynamic from 'next/dynamic';

const RelatedArticles = dynamic(() => import('../../components/RelatedArticles'), {
  ssr: false,
  loading: () => <div style={{ height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading related articles...</div>
});

// Helper to get optimized image source for featured image
const getOptimizedImageSrc = (imageNode) => {
  if (!imageNode) return null;
  
  const sizes = imageNode.mediaDetails?.sizes || [];
  
  // For article featured images, prioritize large or medium_large for good quality but smaller size
  const large = sizes.find(size => size.name === 'large');
  const mediumLarge = sizes.find(size => size.name === 'medium_large');
  const medium = sizes.find(size => size.name === 'medium');
  
  // Return optimized size, fallback to full resolution
  return large?.sourceUrl || mediumLarge?.sourceUrl || medium?.sourceUrl || 
         imageNode.mediaItemUrl || imageNode.sourceUrl;
};

const normalizeSlug = (value = '') =>
  value
    .toString()
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

export default function Article({ data, sharedData }) {

  // Use shared data for header/footer components
  const { generalSettings, headerMenuItems, footerMenuItems, mediaboosterSettings } = sharedData || {};
  const { posts } = data || {};
  const post = posts?.nodes?.[0];

  if (!post) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h1>Article not found</h1>
        <p>The article you're looking for doesn't exist.</p>
      </div>
    );
  }

  const { title: siteTitle, description: siteDescription } = generalSettings || {};
  const primaryMenu = headerMenuItems?.nodes ?? [];
  const footerMenu = footerMenuItems?.nodes ?? [];
  const { title, content, featuredImage, date, author, categories, tags } = post;
  const lightLogo = mediaboosterSettings?.mediaboosterSettingsFields?.logo?.node;
  const footerLogo = mediaboosterSettings?.mediaboosterSettingsFields?.footerLogo?.node;
  const employees = data?.employees?.nodes || [];
  const tagSlugs = (tags?.nodes?.map((tag) => normalizeSlug(tag.slug || tag.name)).filter(Boolean) || []);
  const matchedEmployee = employees.find((employee) => {
    const employeeSlug = normalizeSlug(employee?.slug);
    const employeeUriSlug = normalizeSlug((employee?.uri || '').split('/').filter(Boolean).pop() || '');
    const employeeTitleSlug = normalizeSlug(employee?.title || '');

    return (
      (employeeSlug && tagSlugs.includes(employeeSlug)) ||
      (employeeUriSlug && tagSlugs.includes(employeeUriSlug)) ||
      (employeeTitleSlug && tagSlugs.includes(employeeTitleSlug))
    );
  }) || null;

  // Format the date
  const formattedDate = date ? new Date(date).toLocaleDateString('no-NO', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }) : '';

  // Calculate reading time (rough estimate: 200 words per minute)
  const wordCount = content?.replace(/<[^>]*>/g, '').split(/\s+/).length || 0;
  const readingTime = Math.ceil(wordCount / 200);

  // Get optimized featured image URL for preloading
  const featuredImageSrc = featuredImage?.node ? getOptimizedImageSrc(featuredImage.node) : null;

  // Extract JSON-LD schema from Rank Math's fullHead
  // This includes Article schema + FAQ schema configured in WordPress
  const extractSchemaFromFullHead = (fullHead) => {
    if (!fullHead) return null;
    
    // Match all JSON-LD script tags
    const schemaRegex = /<script[^>]*type=["']application\/ld\+json["'][^>]*>([\s\S]*?)<\/script>/gi;
    const matches = [];
    let match;
    
    while ((match = schemaRegex.exec(fullHead)) !== null) {
      try {
        matches.push(match[1].trim());
      } catch (e) {
        console.error('Error parsing schema from fullHead:', e);
      }
    }
    
    return matches.length > 0 ? matches : null;
  };

  const rankMathSchemas = extractSchemaFromFullHead(post?.seo?.fullHead);

  return (
    <>
      <Head>
        {/* Preload critical featured image for faster loading */}
        {featuredImageSrc && (
          <link 
            rel="preload" 
            href={featuredImageSrc} 
            as="image" 
            fetchpriority="high"
          />
        )}
        {/* Preconnect to WordPress domain for faster image loading */}
        <link rel="preconnect" href="https://mediaboosterhe.wpenginepowered.com" />
        <link rel="dns-prefetch" href="https://mediaboosterhe.wpenginepowered.com" />
        {/* Inject Rank Math schema (Article + FAQ) directly from WordPress */}
        {rankMathSchemas && rankMathSchemas.map((schema, index) => (
          <script
            key={`rankmath-schema-${index}`}
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: schema }}
          />
        ))}
      </Head>
      <SEO seo={{
        ...post?.seo,
        canonicalUrl: `https://mediabooster.no/artikler/${post.slug}/`
      }} />
      <Header
        title={siteTitle}
        description={siteDescription}
        menuItems={primaryMenu}
        lightLogo={lightLogo}
        sharedData={sharedData}
      />
      <Main>
        <article className={styles.article}>
          <div className={styles.container}>
            <div className={styles.articleMeta}>
              <div className={styles.categoriesRow}>
                {categories?.nodes?.map((category, index) => (
                  <React.Fragment key={category.categoryId}>
                    <span className={styles.category}>
                      {category.name}
                    </span>
                    {index < categories.nodes.length - 1 && ' '}
                  </React.Fragment>
                ))}
              </div>
              <div className={styles.metaRow}>
                <span className={styles.date}>{formattedDate}</span>
                <span className={styles.readingTime}>{readingTime} minutter</span>
              </div>
            </div>

            <h1 className={styles.title}>{title}</h1>

            {featuredImage?.node && (
              <div className={styles.featuredImage}>
                <Image
                  src={getOptimizedImageSrc(featuredImage.node)}
                  alt={featuredImage.node.altText || title}
                  width={1024}
                  height={576}
                  priority
                  quality={75}
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 90vw, 1024px"
                  placeholder="blur"
                  blurDataURL="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAyNCIgaGVpZ2h0PSI1NzYiIHZlcnNpb249IjEuMSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAyNCIgaGVpZ2h0PSI1NzYiIGZpbGw9IiNlZWUiLz48L3N2Zz4="
                  style={{
                    width: '100%',
                    height: 'auto',
                    objectFit: 'cover'
                  }}
                />
              </div>
            )}

            <div className={styles.contentWrapper}>
              <div className={styles.content} dangerouslySetInnerHTML={{ __html: content }} />

              <aside className={styles.sidebar}>
                <div className={styles.sidebarStack}>
                  <section className={styles.postContactSection}>
                    <ContactBoxes
                      employee={matchedEmployee}
                      email={matchedEmployee?.ansatte?.email ? matchedEmployee.ansatte.email.replace(/<[^>]*>/g, '') : 'post@mediabooster.no'}
                      showHeading={false}
                      compact
                    />
                  </section>

                  <section id="book-mote" className={styles.calendlySection} aria-label="Book et møte">
                    <h3 className={styles.calendlyHeading}>Snakk med en rådgiver</h3>
                    <div className={styles.calendlyEmbedWrapper}>
                      <iframe
                        src="https://calendly.com/sales-mediabooster/artikler-30-min?hide_event_type_details=1&hide_gdpr_banner=1"
                        title="Book møte med Mediabooster"
                        className={styles.calendlyIframe}
                      />
                    </div>
                  </section>
                </div>
              </aside>
            </div>
          </div>
        </article>
        
        <button
          className={styles.mobileBookBtn}
          onClick={() => document.getElementById('book-mote')?.scrollIntoView({ behavior: 'smooth' })}
          aria-label="Snakk med en rådgiver"
        >
          Snakk med en rådgiver
        </button>

        <RelatedArticles currentPost={post} />

      </Main>

      <Footer 
        title={siteTitle} 
        menuItems={footerMenu}
        lightLogo={lightLogo}
        sharedData={sharedData}
      />
    </>
  );
}

// Add getServerSideProps for server-side data fetching with shared data
export async function getServerSideProps({ params }) {
  const { slug } = params;
  
  const { getApolloClient } = await import('@faustwp/core');
  const apolloClient = getApolloClient();
  
  try {
    // Fetch article data and shared data in parallel
    const [articleResult, sharedResult] = await Promise.all([
      // Fetch article-specific data
      apolloClient.query({
        query: gql`
          ${ArtikkelSEOFragment}
          ${OptimizedImageFragment}
          query GetArticleBySlug($slug: String!) {
            posts(where: { name: $slug }) {
              nodes {
                ...ArtikkelSEOFragment
                databaseId
                title
                content
                date
                slug
                author {
                  node {
                    name
                  }
                }
                categories {
                  nodes {
                    categoryId
                    name
                    slug
                  }
                }
                tags {
                  nodes {
                    name
                    slug
                  }
                }
                featuredImage {
                  node {
                    ...OptimizedImageFragment
                  }
                }
              }
            }
            employees: ansatte(first: 100, where: { language: NB }) {
              nodes {
                title
                slug
                uri
                ansattId
                ansatte {
                  employeePhoto {
                    node {
                      ...OptimizedImageFragment
                    }
                  }
                  role
                  email
                  linkedin
                }
              }
            }
          }
        `,
        variables: { slug },
        fetchPolicy: 'no-cache'
      }),
      
      // Fetch shared data using exact same queries as components
      apolloClient.query({
        query: gql`
          ${BlogInfoFragment}
          ${NavigationMenu.fragments.entry}
          ${MediaBoosterSettingsFragment}
          query GetSharedArticleData(
            $headerLocation: MenuLocationEnum,
            $footerLocation: MenuLocationEnum
          ) {
            generalSettings {
              ...BlogInfoFragment
            }
            ...MediaBoosterSettingsFragment
            headerMenuItems: menuItems(first: 100, where: { location: $headerLocation }) {
              nodes {
                ...NavigationMenuItemFragment
              }
            }
            footerMenuItems: menuItems(first: 100, where: { location: $footerLocation }) {
              nodes {
                ...NavigationMenuItemFragment
              }
            }
            
            # Footer data - exact same query as Footer component
            tjenester(first: 100, where: { taxQuery: { taxArray: { terms: "4", field: TAXONOMY_ID } } }) {
              nodes {
                id
                title
                slug
                uri
                terms {
                  nodes {
                    id
                    name
                  }
                }
                tjenesterPost {
                  serviceDescription
                }
              }
            }
            
            # Frontpage tjenesteboks data used by Header
            page(id: "14", idType: DATABASE_ID) {
              frontPage {
                tjenesteboks {
                  tjenestenavn
                  underoverskrift
                  link
                }
              }
            }
          }
        `,
        variables: {
          headerLocation: MENUS.PRIMARY_LOCATION,
          footerLocation: MENUS.FOOTER_LOCATION,
        },
        fetchPolicy: 'no-cache'
      })
    ]);

    const article = articleResult.data?.posts?.nodes?.[0];
    const sharedData = sharedResult.data;

    // Return 404 if article is not found
    if (!article) {
      return {
        notFound: true,
      };
    }

    // Redirect to blog URL if this is a blog post
    if (isBlogPost(article)) {
      return {
        redirect: {
          destination: `/blogg/${slug}`,
          permanent: false,
        },
      };
    }

    return {
      props: {
        data: articleResult.data,
        sharedData,
      },
    };
  } catch (error) {
    console.error('Error fetching article data:', error);
    return {
      notFound: true,
    };
  }
} 